/**
 * SandboxPanel
 *
 * Contextual right panel for the Sandbox page. Shows canvas surfaces,
 * legacy entity/artifact previews, or the thoughts timeline via tabs.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { AssistantRuntime } from '@assistant-ui/react';
import type { MutableRefObject } from 'react';
import { X, Loader2, AlertCircle, Maximize2, Minimize2 } from 'lucide-react';
import { useUIEvent } from '../../contexts/UIEventContext';
import { useThoughts } from '../../contexts/ThoughtsContext';
import { useCanvases } from '../../contexts/CanvasContext';
import { ThoughtsPanel } from '../../components/assistant-ui/thinking-timeline';
import { groupPartsIntoSteps } from '../../components/assistant-ui/agent-chain-of-thought';
import { stepsToIterations, buildContentFromIterations } from '../../utils/content-builder';
import { spaceApi } from '../../services/api';
import { useDispatchAction } from '../../hooks/useDispatchAction';
import { CanvasPanel } from '../../components/canvas/CanvasPanel';
import EntityView from '../../components/canvas/EntityView';
import ArtifactRenderer from './ArtifactRenderer';

type Tab = 'canvas' | 'thoughts';

interface SandboxPanelProps {
  runtime?: AssistantRuntime;
  agentId?: string;
  conversationIdRef?: MutableRefObject<string | null> | MutableRefObject<string | undefined>;
}

const WIDTH_PRESETS = [480, 0.5]; // px or viewport fraction

export default function SandboxPanel({ runtime: _runtime, agentId, conversationIdRef }: SandboxPanelProps) {
  const { canvasState, dispatchUIEvent } = useUIEvent();
  const { thoughts, activeThoughtId, thoughtsActive, setThoughtsActive, updateThoughtsSteps, registerPanel } = useThoughts();
  const { hasCanvases, scrollToSurface, spaceId: canvasSpaceId } = useCanvases();

  // Signal that a thoughts panel exists here so the collapsed "Thought for Xs"
  // CoT header renders as a clickable control (it's plain text elsewhere).
  useEffect(() => registerPanel(), [registerPanel]);

  // Resizable panel state
  const [panelWidth, setPanelWidth] = useState(() => window.innerWidth * 0.5);
  const isDragging = useRef(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      const newWidth = window.innerWidth - ev.clientX;
      setPanelWidth(Math.max(320, Math.min(newWidth, window.innerWidth * 0.5)));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const cyclePreset = useCallback(() => {
    const resolvedPresets = WIDTH_PRESETS.map(p => (p < 1 ? window.innerWidth * p : p));
    const currentIdx = resolvedPresets.findIndex(p => Math.abs(panelWidth - p) < 20);
    const nextIdx = (currentIdx + 1) % resolvedPresets.length;
    setPanelWidth(resolvedPresets[nextIdx]);
  }, [panelWidth]);

  const isExpanded = panelWidth > 500;

  // On-demand fetch for non-last thoughts that only have placeholder steps
  const fetchedRunIds = useRef(new Set<string>());

  useEffect(() => {
    if (!activeThoughtId || !thoughts?.runId || thoughts.isRunning) return;

    const isPlaceholder =
      thoughts.steps.length === 1 &&
      thoughts.steps[0].tools.length === 0 &&
      thoughts.steps[0].reasoning.length <= 1 &&
      (thoughts.steps[0].reasoning.length === 0 ||
        String((thoughts.steps[0].reasoning[0] as { text?: string })?.text ?? '').replace(/\u200b/g, '').trim() === '');

    if (!isPlaceholder) return;
    if (fetchedRunIds.current.has(thoughts.runId)) return;

    const runId = thoughts.runId;
    const thoughtId = activeThoughtId;
    fetchedRunIds.current.add(runId);

    if (!canvasSpaceId) return;
    spaceApi(canvasSpaceId).runs.get(runId).then((run) => {
      if (!run.steps?.length) return;
      const iterations = stepsToIterations(run.steps);
      const hasFinal = run.status === 'completed';
      const content = buildContentFromIterations(iterations, hasFinal);
      const richSteps = groupPartsIntoSteps(content as readonly { type: string; [key: string]: unknown }[]);
      if (richSteps.length > 0) {
        updateThoughtsSteps(thoughtId, richSteps);
      }
    }).catch((err) => {
      console.warn('[SandboxPanel] Failed to fetch run details for', runId, err);
      fetchedRunIds.current.delete(runId);
    });
  }, [activeThoughtId, thoughts, updateThoughtsSteps]);

  const [activeTab, setActiveTab] = useState<Tab>('canvas');
  const [prevThoughtsActive, setPrevThoughtsActive] = useState(false);
  const [prevHasCanvases, setPrevHasCanvases] = useState(false);

  const hasPreview =
    canvasState.activeEntity ||
    canvasState.activeArtifact ||
    canvasState.artifactLoading ||
    canvasState.artifactError;
  const hasThoughts = thoughtsActive && thoughts && (thoughts.steps?.length ?? 0) > 0;
  const hasContent = hasCanvases || hasPreview;

  // Auto-switch to Thoughts tab when thoughtsActive becomes true
  if (thoughtsActive && !prevThoughtsActive) {
    setActiveTab('thoughts');
  }
  if (thoughtsActive !== prevThoughtsActive) {
    setPrevThoughtsActive(thoughtsActive);
  }

  // Auto-switch to Canvas tab when canvas surfaces first arrive
  if (hasCanvases && !prevHasCanvases) {
    setActiveTab('canvas');
  }
  if (hasCanvases !== prevHasCanvases) {
    setPrevHasCanvases(hasCanvases);
  }

  // Auto-switch to Canvas tab when a scroll-to-surface request arrives
  useEffect(() => {
    if (scrollToSurface) {
      setActiveTab('canvas');
    }
  }, [scrollToSurface]);

  // Handle user actions on canvas surfaces
  const handleAction = useDispatchAction(agentId, canvasSpaceId, conversationIdRef);

  if (!hasContent && !hasThoughts) return null;

  const showTabs = hasContent && hasThoughts;
  const effectiveTab = showTabs ? activeTab : (hasContent ? 'canvas' : 'thoughts');

  const title = effectiveTab === 'thoughts'
    ? 'Thoughts'
    : hasCanvases
      ? 'Canvas'
      : canvasState.activeArtifact?.title
        ?? canvasState.activeEntity?.entityType
        ?? (canvasState.artifactLoading ? 'Loading...' : canvasState.artifactError ? 'Error' : 'Preview');

  const handleClose = () => {
    dispatchUIEvent({ eventType: 'clear_canvas', data: {} });
    setThoughtsActive(false);
  };

  const panelActions = (
    <>
      <button
        type="button"
        onClick={cyclePreset}
        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        title={isExpanded ? 'Collapse panel' : 'Expand panel'}
      >
        {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={handleClose}
        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </>
  );

  // When showing canvases without thoughts, CanvasPanel provides its own header
  const showPanelHeader = showTabs || effectiveTab === 'thoughts' || (effectiveTab === 'canvas' && !hasCanvases);

  return (
    <div className="relative flex shrink-0 flex-col border-l border-border bg-background" style={{ width: panelWidth }}>
      {/* Drag handle */}
      <div
        className="absolute left-0 top-0 z-10 h-full w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50"
        onMouseDown={handleDragStart}
      />
      {/* Header — hidden when CanvasPanel provides its own */}
      {showPanelHeader && (
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          {showTabs ? (
            <div className="flex gap-1">
              <button
                type="button"
                className={`rounded px-2 py-1 text-sm font-medium ${
                  activeTab === 'canvas'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('canvas')}
              >
                {hasCanvases ? 'Canvas' : 'Preview'}
              </button>
              <button
                type="button"
                className={`rounded px-2 py-1 text-sm font-medium ${
                  activeTab === 'thoughts'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('thoughts')}
              >
                Thoughts
              </button>
            </div>
          ) : (
            <h3 className="text-sm font-semibold text-foreground capitalize">
              {title}
            </h3>
          )}
          <div className="flex items-center gap-1">
            {panelActions}
          </div>
        </div>
      )}

      {/* Content */}
      {effectiveTab === 'canvas' && (
        <>
          {hasCanvases ? (
            <CanvasPanel onAction={handleAction} headerActions={!showPanelHeader ? panelActions : undefined} />
          ) : (
            /* Legacy entity/artifact preview when no canvases */
            <>
              {canvasState.artifactLoading && (
                <div className="flex flex-1 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {canvasState.artifactError && (
                <div className="flex flex-1 items-center justify-center p-4">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <AlertCircle className="h-8 w-8" />
                    <span className="text-sm">{canvasState.artifactError}</span>
                  </div>
                </div>
              )}
              {canvasState.activeEntity && (
                <EntityView
                  entityType={canvasState.activeEntity.entityType}
                  entityId={canvasState.activeEntity.entityId}
                  section={canvasState.activeEntity.section}
                  highlights={canvasState.highlights}
                />
              )}
              {canvasState.activeArtifact && (
                <ArtifactRenderer artifact={canvasState.activeArtifact} />
              )}
            </>
          )}
        </>
      )}
      {effectiveTab === 'thoughts' && thoughts && (
        <ThoughtsPanel
          steps={thoughts.steps}
          elapsed={thoughts.elapsed}
          isRunning={thoughts.isRunning}
          error={thoughts.error}
        />
      )}
    </div>
  );
}
